import { ArticleItem } from '../../types';

export const medicalDeviceVigilancePlanGuideEn: ArticleItem = {
  slug: "medical-device-vigilance-plan-guide",
  title: "Guidelines for Writing Medical Device Vigilance Plans (Trial) Released",
  summary: "The trial guideline clarifies core requirements for medical device vigilance plans, covering risk management, data collection, risk analysis, risk control, reporting, and plan review.",
  seo: {
    title: "Medical Device Vigilance Plan Writing Guide Released | HL Medical",
    description: "The trial guideline for medical device vigilance plans clarifies requirements for risk management, data collection, risk analysis, risk control, vigilance reporting, and plan review.",
    keywords: "medical device vigilance plan, post-market surveillance, medical device risk management, adverse event monitoring, medical device compliance",
    canonicalUrl: "https://hlmedicalconsult.cn/en/articles/medical-device-vigilance-plan-guide",
    alternateUrls: {
      en: "https://hlmedicalconsult.cn/en/articles/medical-device-vigilance-plan-guide",
      cn: "https://hlmedicalconsult.cn/cn/articles/medical-device-vigilance-plan-guide",
      xDefault: "https://hlmedicalconsult.cn/en/articles/medical-device-vigilance-plan-guide"
    },
    ogTitle: "Guidelines for Writing Medical Device Vigilance Plans Released",
    ogDescription: "The guideline supports a shift from compliance-driven post-market work to risk-driven medical device vigilance.",
    ogType: "article"
  },
  date: "2026-05-08",
  category: "Post-market Surveillance",
  readTime: "4 min read",
  sourceName: "HIGH-USABILITY",
  originalUrl: "https://www.highusability.com",
  sourceNote: "source: High Usability: www.high-usability.com",
  content: [
    { type: "section", text: "01" },
    { type: "heading", text: "Basic Requirements" },
    { type: "paragraph", text: "The guideline makes clear that the medical device vigilance plan is an important document within the vigilance system and should follow risk management principles." },
    { type: "paragraph", text: "At minimum, it should cover product scope, vigilance objectives, data collection, risk analysis, risk control, vigilance reporting, and plan review." },
    { type: "paragraph", text: "Companies should define the scope of vigilance data collection and risk evaluation methods based on product risk characteristics, take timely control measures, and ensure effective implementation of vigilance activities." },
    { type: "paragraph", text: "The vigilance plan should also state whether active monitoring will be conducted, whether risk information for similar products will be searched, and whether regular risk identification activities will be performed. Relevant vigilance activities should then be implemented strictly according to the plan." },
    { type: "section", text: "02" },
    { type: "heading", text: "Core Content" },
    { type: "paragraph", text: "The guideline builds the overall framework of the medical device vigilance plan around risk management. The key content includes:" },
    { type: "bullet", text: "Product scope and vigilance objectives: Companies should define product series, structural principles, target users, and use scenarios. The core objective is to identify risks that were not fully discovered before market approval, with focus on risk trend changes, updates to known risks, and relevant use information." },
    { type: "bullet", text: "Data collection and risk analysis: The guideline emphasizes multiple data sources, including adverse event monitoring, user feedback, and literature. It also requires standardized methods to evaluate risk severity and probability, ensuring consistency with the existing risk management system." },
    { type: "bullet", text: "Risk control and vigilance reporting: Companies should establish records for risk identification and control, and prepare trend reports or periodic safety update reports when risk trends become abnormal, supporting closed-loop risk management." },
    { type: "bullet", text: "Plan review: The guideline requires regular evaluation of vigilance plan implementation, with necessary revisions and improvements based on review results." },
    { type: "section", text: "03" },
    { type: "heading", text: "Industry Impact" },
    { type: "paragraph", text: "Overall, implementation of the Guidelines for Writing Medical Device Vigilance Plans (Trial) will help the industry shift from compliance-driven practices to risk-driven management." },
    { type: "paragraph", text: "It can improve post-market safety and effectiveness of medical devices and also provides important support for more science-based regulation." }
  ]
};

export const medicalDeviceVigilancePlanGuideCn: ArticleItem = {
  slug: "medical-device-vigilance-plan-guide",
  title: "《医疗器械警戒计划撰写指南（试行）》发布",
  summary: "指南明确医疗器械警戒计划的基本要求和核心内容，推动上市后监管从合规驱动走向风险驱动。",
  seo: {
    title: "《医疗器械警戒计划撰写指南（试行）》发布 | 赫莱医药",
    description: "《医疗器械警戒计划撰写指南（试行）》明确产品范围、警戒目标、数据收集、风险分析、风险控制、警戒报告及计划评审等要求。",
    keywords: "医疗器械警戒计划, 医疗器械上市后监管, 不良事件监测, 医疗器械风险管理, 医疗器械合规",
    canonicalUrl: "https://hlmedicalconsult.cn/cn/articles/medical-device-vigilance-plan-guide",
    alternateUrls: {
      en: "https://hlmedicalconsult.cn/en/articles/medical-device-vigilance-plan-guide",
      cn: "https://hlmedicalconsult.cn/cn/articles/medical-device-vigilance-plan-guide",
      xDefault: "https://hlmedicalconsult.cn/en/articles/medical-device-vigilance-plan-guide"
    },
    ogTitle: "《医疗器械警戒计划撰写指南（试行）》发布",
    ogDescription: "指南推动行业从“合规驱动”向“风险驱动”转型，提升医疗器械上市后安全性与有效性。",
    ogType: "article"
  },
  date: "2026-05-08",
  category: "上市后监管",
  readTime: "约4分钟",
  sourceName: "HIGH-USABILITY",
  originalUrl: "https://www.highusability.com",
  sourceNote: "source: High Usability: www.high-usability.com",
  content: [
    { type: "section", text: "01" },
    { type: "heading", text: "基本要求" },
    { type: "paragraph", text: "指南明确，医疗器械警戒计划作为警戒体系的重要文件，应符合风险管理原则，至少涵盖产品范围、警戒目标、数据收集、风险分析、风险控制、警戒报告及计划评审等内容。" },
    { type: "paragraph", text: "同时，企业需根据产品风险特征，明确警戒数据收集范围与风险评估方法，及时采取控制措施，确保警戒活动有效实施。" },
    { type: "paragraph", text: "此外，还应在警戒计划中说明是否开展主动监测、是否进行同类产品风险信息检索，以及是否定期开展风险识别等内容，并严格按照计划执行相关警戒活动。" },
    { type: "section", text: "02" },
    { type: "heading", text: "核心内容" },
    { type: "paragraph", text: "指南围绕风险管理主线，构建了医疗器械警戒计划的整体框架，主要包括以下内容：" },
    { type: "bullet", text: "在产品范围与警戒目标方面，要求明确产品系列、结构原理、适用人群及使用场景，并以识别上市前未充分发现的风险为核心目标，重点关注风险变化趋势、已知风险更新及相关使用信息。" },
    { type: "bullet", text: "在数据收集与风险分析方面，强调多渠道数据来源，包括不良事件监测、用户反馈及文献资料等，并要求采用规范化方法对风险严重程度与发生概率进行评估，确保与既有风险管理体系保持一致。" },
    { type: "bullet", text: "在风险控制与警戒报告方面，需建立风险识别与控制的记录机制，并明确在风险趋势异常时形成趋势报告或定期安全更新报告，保障风险管理的闭环运行。" },
    { type: "bullet", text: "在计划评审方面，指南要求定期对警戒计划执行情况进行评估，根据评审结果对计划进行必要的修订和完善。" },
    { type: "section", text: "03" },
    { type: "heading", text: "行业影响" },
    { type: "paragraph", text: "总体来看，《医疗器械警戒计划撰写指南（试行）》的实施将推动行业从“合规驱动”向“风险驱动”转型，有助于提升医疗器械上市后的安全性与有效性，也为监管科学化提供了重要支撑。" }
  ]
};
